import React from 'react';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { getUserInitials } from '../../util/userHelpers';

/**
 * P8D: Renders a user's anonymized initials ("F. L." format) for
 * public contexts. This component is used for every "other user"
 * surface (reviews, messaging, transaction timeline, inbox, etc.),
 * so it must never expose the full display name.
 *
 * Private contexts where the full name is expected (user's own
 * profile settings, payment autofill, "Hola {firstName}" greeting)
 * should NOT route through this component — they read
 * profile.firstName / profile.lastName / profile.displayName
 * directly.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {propTypes.user|propTypes.currentUser} props.user - The user object
 * @param {string} [props.deletedUserDisplayName] - Override string for deleted users
 * @param {string} [props.bannedUserDisplayName] - Override string for banned users
 * @returns {JSX.Element} User display name component
 */
const UserDisplayName = props => {
  const {
    rootClassName,
    className,
    user,
    intl,
    deletedUserDisplayName,
    bannedUserDisplayName,
  } = props;
  const hasAttributes = user && user.attributes;
  const userIsDeleted = hasAttributes && user.attributes.deleted;
  const userIsBanned = hasAttributes && user.attributes.banned;

  const deletedUserDisplayNameInUse = deletedUserDisplayName
    ? deletedUserDisplayName
    : intl.formatMessage({
        id: 'UserDisplayName.deleted',
      });

  const bannedUserDisplayNameInUse = bannedUserDisplayName
    ? bannedUserDisplayName
    : intl.formatMessage({
        id: 'UserDisplayName.banned',
      });

  const initials = getUserInitials(user);

  const displayName = initials
    ? initials
    : userIsDeleted
    ? deletedUserDisplayNameInUse
    : userIsBanned
    ? bannedUserDisplayNameInUse
    : null;

  const classes = classNames(rootClassName, className);
  return <span className={classes}>{displayName}</span>;
};

export default UserDisplayName;
